/**
 * The answers the logbook page needs, kept on the device after "Prepare for offline", so the
 * checklists, today's sailings, the emergency card and the journal can be read without a
 * connection.
 *
 * - Only a short list of logbook answers is kept (never guest lists, bookings or payments).
 * - Each answer is stored under the user it was fetched for, and read back only for that user.
 * - Once the device is prepared, every successful read of one of these answers refreshes the copy.
 * - A copy is valid for a limited time; an old one is shown as old, not as current.
 * - No token or credential is stored with them.
 */

import { all, available, clearStore, get, put, remove, requestPersistence } from "./db";

/** The answers that are kept: [path prefix, may carry the vessel id in the query]. */
const KEPT = [
  "/logbook/vessels",
  "/logbook/voyages",
  "/logbook/rules",
  "/logbook/checklists",
  "/logbook/journal",
  "/logbook/compliance",
  "/logbook/engines/status",
  "/logbook/fuel/forecast",
];

/** Never kept, even if a prefix above would match (personal data). */
const NEVER = ["/guests"];

/** How long a prepared copy may be used without being refreshed. */
export const VALID_MS = 7 * 24 * 3600 * 1000;

export const isKept = (endpoint: string): boolean => {
  const path = endpoint.split("?")[0];
  return !NEVER.some((n) => path.includes(n)) && KEPT.some((k) => path === k || path.startsWith(`${k}/`));
};

/** The key an answer is stored under: the path, and the vessel when the answer is for one. The dates of a time window are left out, so a later day finds the same copy. */
export function readKey(userId: string, endpoint: string): string {
  const [path, query = ""] = endpoint.split("?");
  const vessel = new URLSearchParams(query).get("vesselId");
  return `${userId}|${path}${vessel ? `?vesselId=${vessel}` : ""}`;
}

interface StoredRead {
  key: string;
  userId: string;
  at: string;
  data: unknown;
}

interface Prepared {
  key: string;
  userId: string;
  at: string;
  appVersion: string;
  vesselId?: string;
}

const PREPARED_KEY = (userId: string) => `prepared|${userId}`;

export async function preparedFor(userId: string): Promise<Prepared | undefined> {
  if (!available()) return undefined;
  try {
    return await get<Prepared>("meta", PREPARED_KEY(userId));
  } catch {
    return undefined;
  }
}

/** Keep an answer, if this device has been prepared for the user. A failure to store is not an error for the page. */
export async function keepRead(userId: string, endpoint: string, data: unknown): Promise<void> {
  if (!available() || !isKept(endpoint)) return;
  try {
    if (!(await preparedFor(userId))) return;
    await put<StoredRead>("reads", { key: readKey(userId, endpoint), userId, at: new Date().toISOString(), data });
  } catch {
    /* storage full or refused: the page still works online */
  }
}

export interface OfflineRead<T> {
  data: T;
  /** when the copy was fetched */
  at: string;
  expired: boolean;
}

/** The kept copy of an answer for this user, or nothing. */
export async function offlineRead<T>(userId: string, endpoint: string): Promise<OfflineRead<T> | undefined> {
  if (!available() || !isKept(endpoint)) return undefined;
  try {
    const row = await get<StoredRead>("reads", readKey(userId, endpoint));
    if (!row || row.userId !== userId) return undefined;
    return { data: row.data as T, at: row.at, expired: Date.now() - Date.parse(row.at) > VALID_MS };
  } catch {
    return undefined;
  }
}

export interface PrepareResult {
  at: string;
  /** the answers that were stored and read back */
  stored: string[];
  /** the answers that could not be fetched or stored */
  missing: string[];
  /** the size and a note of each stored answer: what was confirmed stored, not what happens to be in the cache */
  details: { endpoint: string; bytes: number; note?: string }[];
  complete: boolean;
  persistent: boolean;
}

/**
 * Prepare this device: fetch every answer in the list, store it, read it back, and only then say it
 * is ready. Anything missing is reported by name; a prepared device with a gap is not "ready".
 */
export async function prepareDevice(
  userId: string,
  appVersion: string,
  vesselId: string,
  fetchers: { endpoint: string; fetch: () => Promise<unknown>; /** a short note kept with the answer in the result (for example the time of a forecast) */ describe?: (data: unknown) => string | undefined }[],
): Promise<PrepareResult> {
  if (!available()) throw new Error("This browser cannot store data for offline use");
  const at = new Date().toISOString();
  await put<Prepared>("meta", { key: PREPARED_KEY(userId), userId, at, appVersion, vesselId });
  const stored: string[] = [];
  const missing: string[] = [];
  const details: { endpoint: string; bytes: number; note?: string }[] = [];
  for (const f of fetchers) {
    try {
      const data = await f.fetch();
      await put<StoredRead>("reads", { key: readKey(userId, f.endpoint), userId, at, data });
      const back = await get<StoredRead>("reads", readKey(userId, f.endpoint));
      if (back && back.userId === userId) {
        stored.push(f.endpoint);
        details.push({ endpoint: f.endpoint, bytes: JSON.stringify(back.data).length, note: f.describe?.(back.data) });
      } else missing.push(f.endpoint);
    } catch {
      missing.push(f.endpoint);
    }
  }
  const persistent = await requestPersistence();
  return { at, stored, missing, details, complete: missing.length === 0 && stored.length === fetchers.length, persistent };
}

/** Remove what was kept for a user (on logout or on request). The queue of unsent entries is not touched. */
export async function clearReads(userId?: string): Promise<void> {
  if (!available()) return;
  try {
    if (!userId) {
      await clearStore("reads");
      return;
    }
    for (const row of await all<StoredRead>("reads")) if (row.userId === userId) await remove("reads", row.key);
    await remove("meta", PREPARED_KEY(userId));
  } catch {
    /* nothing to clear */
  }
}
